import { useState, type FormEvent } from "react";

import { Button, Card, CardBody, CardHead } from "@/components/common";
import { Guard } from "@/auth/Guard";
import { useSetClientAddress, useSetClientBank } from "@/hooks/useClients";
import { extractErrorMessage } from "@/services/axiosConfig";
import type { BankAccount, Client, ClientAddress } from "@/types/client";

const EMPTY_ADDRESS: ClientAddress = {
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  city: "",
  state: "",
  zip_code: "",
};

const EMPTY_BANK: BankAccount = {
  bank_name: "",
  agency: "",
  account: "",
  holder_name: "",
  pix_key: "",
};

/** Aba "Endereço & banco" do detalhe do cliente. Endereço é texto plano; banco é
 * PII (ADR-0011): em claro só com pii.read.bank — sem isso, mostramos o mascarado
 * como referência e o form começa vazio (salvar sobrescreve). */
export function ClientFinanceTab({
  workspaceId,
  client,
}: {
  workspaceId: number;
  client: Client;
}) {
  const setAddress = useSetClientAddress(workspaceId, client.id);
  const setBank = useSetClientBank(workspaceId, client.id);

  const [addr, setAddr] = useState<ClientAddress>({ ...EMPTY_ADDRESS, ...(client.address ?? {}) });
  const [bank, setBankState] = useState<BankAccount>({ ...EMPTY_BANK, ...(client.bank ?? {}) });
  const [addrError, setAddrError] = useState<string | null>(null);
  const [bankError, setBankError] = useState<string | null>(null);

  const canReadBank = client.bank != null;
  const masked = client.bank_masked;

  async function saveAddress(e: FormEvent) {
    e.preventDefault();
    setAddrError(null);
    try {
      await setAddress.mutateAsync(addr);
    } catch (err) {
      setAddrError(extractErrorMessage(err));
    }
  }

  async function saveBank(e: FormEvent) {
    e.preventDefault();
    setBankError(null);
    try {
      await setBank.mutateAsync(bank);
    } catch (err) {
      setBankError(extractErrorMessage(err));
    }
  }

  const field = (
    label: string,
    value: string | null,
    onChange: (v: string) => void,
    placeholder = "",
  ) => (
    <label className="form-field">
      <span>{label}</span>
      <input
        className="form-input"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </label>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card>
        <CardHead title="Endereço" />
        <CardBody>
          <form onSubmit={saveAddress} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {addrError && <div className="form-error">{addrError}</div>}
            <div className="onb-field-grid onb-grid-2">
              {field("Logradouro", addr.street, (v) => setAddr((a) => ({ ...a, street: v })))}
              {field("Número", addr.number, (v) => setAddr((a) => ({ ...a, number: v })))}
              {field("Complemento", addr.complement, (v) => setAddr((a) => ({ ...a, complement: v })))}
              {field("Bairro", addr.neighborhood, (v) => setAddr((a) => ({ ...a, neighborhood: v })))}
              {field("Cidade", addr.city, (v) => setAddr((a) => ({ ...a, city: v })))}
              {field("UF", addr.state, (v) => setAddr((a) => ({ ...a, state: v })), "SP")}
              {field("CEP", addr.zip_code, (v) => setAddr((a) => ({ ...a, zip_code: v })), "00000-000")}
            </div>
            <Guard permission="clients.write">
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button variant="primary" type="submit" disabled={setAddress.isPending}>
                  {setAddress.isPending ? "Salvando…" : "Salvar endereço"}
                </Button>
              </div>
            </Guard>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHead
          title="Dados bancários"
          subtitle="PII (ADR-0011) — visível em claro só com permissão pii.read.bank."
        />
        <CardBody>
          {!canReadBank && masked && (
            <p className="td-small" style={{ marginBottom: 12 }}>
              Conta atual (mascarada): {masked.bank_name ?? "—"} · ag. {masked.agency ?? "—"} · cc{" "}
              {masked.account ?? "—"} · PIX {masked.pix_key ?? "—"}. Você não tem permissão para ver
              os dados completos; salvar sobrescreve.
            </p>
          )}
          <form onSubmit={saveBank} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {bankError && <div className="form-error">{bankError}</div>}
            <div className="onb-field-grid onb-grid-2">
              {field("Banco", bank.bank_name, (v) => setBankState((b) => ({ ...b, bank_name: v })))}
              {field("Agência", bank.agency, (v) => setBankState((b) => ({ ...b, agency: v })))}
              {field("Conta", bank.account, (v) => setBankState((b) => ({ ...b, account: v })))}
              {field("Titular", bank.holder_name, (v) =>
                setBankState((b) => ({ ...b, holder_name: v })),
              )}
              {field("Chave PIX", bank.pix_key, (v) => setBankState((b) => ({ ...b, pix_key: v })))}
            </div>
            <Guard permission="clients.write">
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button variant="primary" type="submit" disabled={setBank.isPending}>
                  {setBank.isPending ? "Salvando…" : "Salvar dados bancários"}
                </Button>
              </div>
            </Guard>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
